import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Badge } from "components/ui/badge";
import { CheckCircle, Warning, Wrench, CalendarBlank } from "@phosphor-icons/react";
import { API_URL } from "constants/index";

interface SchedulePublicInfo {
  schedule: {
    id: number;
    name: string;
    description?: string;
    frequency: string;
    nextDueDate?: string;
    asset?: {
      id: string;
      name: string;
      building?: { id: string };
      assetCategory?: { name: string };
    };
    supplier?: { id: number; name: string };
  };
  currentEvent?: {
    id: number;
    status: string;
    supplierReport?: string;
    reporterName?: string;
  };
}

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: "Maandelijks",
  quarterly: "Per kwartaal",
  biannual: "Halfjaarlijks",
  yearly: "Jaarlijks",
  two_yearly: "Tweejaarlijks",
  five_yearly: "Vijfjaarlijks",
  custom: "Aangepast",
};

export const ReportMaintenancePage = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SchedulePublicInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [supplierReport, setSupplierReport] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [reporterPhone, setReporterPhone] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!token) return;
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/maintenance/public/${token}`);
        if (!response.ok) {
          throw new Error("Onderhoudsschema niet gevonden");
        }
        const result = await response.json();
        setData(result);
      } catch (error) {
        setLoadError(
          error instanceof Error
            ? error.message
            : "Kon onderhoudsinformatie niet laden"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchSchedule();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplierReport.trim() || !token) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const response = await fetch(
        `${API_URL}/maintenance/public/${token}/report`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            supplierReport: supplierReport.trim(),
            ...(reporterName ? { reporterName } : {}),
            ...(reporterEmail ? { reporterEmail } : {}),
            ...(reporterPhone ? { reporterPhone } : {}),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || "Kon rapport niet versturen"
        );
      }

      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Er is een fout opgetreden"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-muted-foreground">Laden...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg border bg-white p-6 text-center shadow-sm">
          <Warning className="mx-auto mb-3 h-12 w-12 text-red-500" />
          <h1 className="text-lg font-semibold">Niet gevonden</h1>
          <p className="mt-2 text-sm text-muted-foreground">{loadError}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg border bg-white p-6 text-center shadow-sm">
          <CheckCircle className="mx-auto mb-3 h-12 w-12 text-green-500" />
          <h1 className="text-lg font-semibold">Onderhoudsrapport ingediend</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Bedankt voor uw rapport. De beheerder wordt op de hoogte gesteld.
          </p>
        </div>
      </div>
    );
  }

  const schedule = data?.schedule;
  const currentEvent = data?.currentEvent;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="h-5 w-5 text-orange-600" />
            <h1 className="text-lg font-semibold">Onderhoudsrapport</h1>
          </div>

          {schedule && (
            <div className="mt-3 space-y-2">
              <p className="text-sm font-medium">{schedule.name}</p>
              {schedule.description && (
                <p className="text-xs text-muted-foreground">
                  {schedule.description}
                </p>
              )}
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="text-xs">
                  {FREQUENCY_LABELS[schedule.frequency] || schedule.frequency}
                </Badge>
                {schedule.nextDueDate && (
                  <Badge variant="outline" className="text-xs">
                    <CalendarBlank className="mr-1 h-3 w-3" />
                    {new Date(schedule.nextDueDate).toLocaleDateString("nl-NL")}
                  </Badge>
                )}
              </div>
              {schedule.asset && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{schedule.asset.name}</span>
                  {schedule.asset.assetCategory && (
                    <Badge variant="outline" className="text-xs">
                      {schedule.asset.assetCategory.name}
                    </Badge>
                  )}
                </div>
              )}
              {schedule.supplier && (
                <p className="text-xs text-muted-foreground">
                  Leverancier: {schedule.supplier.name}
                </p>
              )}
            </div>
          )}

          {currentEvent && currentEvent.status === "reported" && (
            <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-xs font-medium text-blue-700">
                Er is al een rapport ingediend
                {currentEvent.reporterName
                  ? ` door ${currentEvent.reporterName}`
                  : ""}
                . U kunt dit bijwerken.
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplierReport">
              Beschrijving van het onderhoud <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="supplierReport"
              value={supplierReport}
              onChange={(e) => setSupplierReport(e.target.value)}
              placeholder="Beschrijf het uitgevoerde onderhoud, bevindingen, opmerkingen..."
              rows={5}
              required
              maxLength={5000}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Uw naam (optioneel)</Label>
            <Input
              id="name"
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
              placeholder="Naam"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres (optioneel)</Label>
            <Input
              id="email"
              type="email"
              value={reporterEmail}
              onChange={(e) => setReporterEmail(e.target.value)}
              placeholder="uw@email.be"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telefoonnummer (optioneel)</Label>
            <Input
              id="phone"
              type="tel"
              value={reporterPhone}
              onChange={(e) => setReporterPhone(e.target.value)}
              placeholder="+32 ..."
            />
          </div>

          {submitError && (
            <p className="text-sm text-red-500">{submitError}</p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || !supplierReport.trim()}
          >
            {isSubmitting ? "Versturen..." : "Rapport versturen"}
          </Button>
        </form>
      </div>
    </div>
  );
};
